import { GenericError } from '../errors/GenericError';
/**
 * Standardized error handler for Redux Toolkit thunks.
 * It will properly serialize GenericError instances to be used as rejected action payloads,
 * which can then be handled in reducers or components.
 *
 * @param error The error object caught in the thunk
 * @param rejectWithValue The rejectWithValue function provided by createAsyncThunk for returning a rejected action with a payload
 */
export const thunkErrorHandler = (error, rejectWithValue) => {
    if (error instanceof GenericError) {
        return rejectWithValue(error.toJSON());
    }
    throw error;
};
/**
 * Utility function to extract a user-friendly error message from various error types.
 * It checks for the presence of a translationKey (for GenericError), message property (for standard Errors),
 * or returns a default message if the error type is unrecognized.
 * @param error The error object from which to extract the message
 * @param t The translation function from useTranslation hook to convert translation keys to user-friendly messages
 * @param fallback The fallback message to use if no specific error message is found
 * @returns A user-friendly error message
 */
export const getErrorMesssage = (error, t, fallback = 'errors.common') => {
    const resolvedError = !(error instanceof GenericError) && error instanceof Object && 'name' in error && error.name === 'GenericError'
        ? GenericError.fromJSON(error)
        : error;
    if (resolvedError instanceof GenericError) {
        const keys = [];
        // If the error has a code, we can try to use a more specific translation key that includes the code
        // (e.g., "errors.facility.create.40301"). This allows for more granular error messages based on specific error codes.
        if (resolvedError.code) {
            keys.push(`${resolvedError.translationKey}.${resolvedError.code}`);
        }
        // Fallback has higher priority than the generic translation key, so we add it next if provided.
        if (fallback) {
            keys.push(fallback);
        }
        keys.push(resolvedError.translationKey);
        return t(keys);
    }
    else if (typeof resolvedError === 'string') {
        return resolvedError;
    }
    else if (resolvedError instanceof Error || (resolvedError instanceof Object && 'message' in resolvedError)) {
        return resolvedError.message;
    }
    return t(fallback);
};
